import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

interface Point {
  x: number;
  y: number;
}

interface Stroke {
  points: Point[];
  color: string;
  width: number;
  timestamp: number;
  opacity: number;
}

export interface PenHighlightOverlayProps {
  isActive: boolean;
  containerRef?: React.RefObject<HTMLIFrameElement | null>;
  isRecording?: boolean;
  onCanvasReady?: (canvas: HTMLCanvasElement) => void;
  drawingMode?: boolean;
  onDrawingModeChange?: (enabled: boolean) => void;
  isFullscreen?: boolean;
}

const PenHighlightOverlay: React.FC<PenHighlightOverlayProps> = ({
  isActive,
  containerRef,
  isRecording = false,
  onCanvasReady,
  drawingMode = false,
  onDrawingModeChange,
  isFullscreen = false,
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [currentStroke, setCurrentStroke] = useState<Point[]>([]);
  const [cursorPosition, setCursorPosition] = useState<Point | null>(null);

  const penColor = "#FF0000";
  const penWidth = 4;
  const FADE_DURATION = 2000;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Expose canvas reference when ready
  useEffect(() => {
    if (canvasRef.current && onCanvasReady && isActive) {
      onCanvasReady(canvasRef.current);
    }
  }, [canvasRef.current, onCanvasReady, isActive]);

  // Toggle drawing mode with 'D' key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.closest(".ProseMirror")
      )
        return;
      if ((e.key === "d" || e.key === "D") && isActive && onDrawingModeChange) {
        onDrawingModeChange(!drawingMode);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive, drawingMode, onDrawingModeChange]);

  // Get iframe drawing area bounds
  const getIframeDimensions = useCallback(() => {
    const iframe = containerRef?.current || document.querySelector("iframe");
    if (!iframe) return null;

    const rect = iframe.getBoundingClientRect();
    const screenWidth = window.innerWidth;

    let navigationBarHeight = 0;
    if (!isFullscreen) {
      navigationBarHeight = 55;
      if (screenWidth >= 2560) navigationBarHeight = 90;
      else if (screenWidth >= 1920) navigationBarHeight = 80;
      else if (screenWidth >= 1440) navigationBarHeight = 65;
      else if (screenWidth >= 1280) navigationBarHeight = 60;
    }

    return {
      left: rect.left,
      top: rect.top + navigationBarHeight,
      width: rect.width,
      height: rect.height - navigationBarHeight,
      iframe: iframe as HTMLIFrameElement,
    };
  }, [containerRef, isFullscreen]);

  // Draw a stroke on canvas
  const drawStroke = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      points: Point[],
      color: string,
      width: number,
      opacity: number
    ) => {
      if (points.length < 2) return;

      ctx.globalAlpha = opacity;
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);

      for (let i = 1; i < points.length; i++) {
        const mid = {
          x: (points[i].x + points[i - 1].x) / 2,
          y: (points[i].y + points[i - 1].y) / 2,
        };
        ctx.quadraticCurveTo(points[i - 1].x, points[i - 1].y, mid.x, mid.y);
      }

      ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
      ctx.stroke();
      ctx.globalAlpha = 1.0;
    },
    []
  );

  // Animation loop — fade strokes out over FADE_DURATION
  const animateStrokes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const now = Date.now();
    let hasActive = false;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const updated = strokes
      .map((stroke) => {
        const elapsed = now - stroke.timestamp;
        if (elapsed < FADE_DURATION) {
          const opacity = 1.0 - elapsed / FADE_DURATION;
          drawStroke(ctx, stroke.points, stroke.color, stroke.width, opacity);
          hasActive = true;
          return { ...stroke, opacity };
        }
        return null;
      })
      .filter((s): s is Stroke => s !== null);

    if (currentStroke.length > 0 && isDrawing) {
      drawStroke(ctx, currentStroke, penColor, penWidth, 1.0);
      hasActive = true;
    }

    if (updated.length !== strokes.length) setStrokes(updated);

    if (hasActive || isDrawing) {
      animationFrameRef.current = requestAnimationFrame(animateStrokes);
    }
  }, [strokes, currentStroke, isDrawing, drawStroke, penColor, penWidth]);

  useEffect(() => {
    if (strokes.length > 0 || isDrawing) {
      if (animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(animateStrokes);
      }
    }
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [strokes, isDrawing, animateStrokes]);


  // Pointer handlers — overlay is pointer-events:auto in draw mode.
  // elementFromPoint trick in pointerDown forwards clicks to buttons below.

  const handlePointerUpInternal = useCallback(() => {
    if (!isDrawing) return;

    if (currentStroke.length > 1) {
      setStrokes((prev) => [
        ...prev,
        {
          points: currentStroke,
          color: penColor,
          width: penWidth,
          timestamp: Date.now(),
          opacity: 1.0,
        },
      ]);
    }

    setIsDrawing(false);
    setCurrentStroke([]);
  }, [isDrawing, currentStroke, penColor, penWidth]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isActive || !drawingMode) return;

      // Check if there is a clickable element below the overlay
      const overlayEl = e.currentTarget;
      overlayEl.style.pointerEvents = "none";
      const elementBelow = document.elementFromPoint(e.clientX, e.clientY);
      overlayEl.style.pointerEvents = "auto";

      if (elementBelow) {
        const clickable = (elementBelow as HTMLElement).closest(
          "button, [role='button'], a, input, select, textarea"
        ) as HTMLElement | null;
        if (clickable) {
          clickable.click();
          return;
        }
      }

      const overlay = overlayRef.current;
      if (!overlay) return;

      const rect = overlay.getBoundingClientRect();
      setIsDrawing(true);
      setCurrentStroke([{ x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    },
    [isActive, drawingMode]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const overlay = overlayRef.current;
      if (!overlay) return;

      const rect = overlay.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (drawingMode) {
        setCursorPosition({ x, y });
      } else {
        setCursorPosition(null);
      }

      if (!isDrawing || !isActive || !drawingMode) return;
      setCurrentStroke((prev) => [...prev, { x, y }]);
    },
    [isDrawing, isActive, drawingMode]
  );

  const handlePointerUp = useCallback(() => {
    handlePointerUpInternal();
  }, [handlePointerUpInternal]);

  const handlePointerLeave = useCallback(() => {
    setCursorPosition(null);
    handlePointerUpInternal();
  }, [handlePointerUpInternal]);

  // Clear when deactivated
  useEffect(() => {
    if (!isActive) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setStrokes([]);
      setCurrentStroke([]);
      setIsDrawing(false);
      setCursorPosition(null);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  }, [isActive]);

  // Clear when recording stops
  useEffect(() => {
    if (!isRecording && strokes.length > 0) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setStrokes([]);
      setCurrentStroke([]);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  }, [isRecording, strokes.length]);

  // Keep canvas sized to the iframe drawing area
  useEffect(() => {
    const resize = () => {
      const dims = getIframeDimensions();
      if (!dims || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = dims.width * dpr;
      canvas.height = dims.height * dpr;
      canvas.style.width = `${dims.width}px`;
      canvas.style.height = `${dims.height}px`;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [getIframeDimensions]);

  if (!isActive) return null;
  const dimensions = getIframeDimensions();
  if (!dimensions) return null;

  return createPortal(
    <div
      ref={overlayRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      style={{
        position: "fixed",
        left: `${dimensions.left}px`,
        top: `${dimensions.top}px`,
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        pointerEvents: drawingMode ? "auto" : "none",
        zIndex: 9997,
        cursor: drawingMode ? "none" : "default",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ pointerEvents: "none", width: "100%", height: "100%" }}
      />

      {/* Red dot pen cursor */}
      {cursorPosition && drawingMode && (
        <div
          style={{
            position: "absolute",
            left: `${cursorPosition.x}px`,
            top: `${cursorPosition.y}px`,
            width: `${penWidth * 2}px`,
            height: `${penWidth * 2}px`,
            borderRadius: "50%",
            backgroundColor: penColor,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            opacity: 0.7,
            border: "2px solid rgba(0,0,0,0.3)",
            boxShadow: "0 0 4px rgba(0,0,0,0.2)",
          }}
        />
      )}
    </div>,
    document.body
  );
};

export default PenHighlightOverlay;
